import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useFontSize } from '../../contexts/FontSizeContext';
import { getTightCellTextSizeClass } from '../../utils/fontSizeUtils';
import { StockTableLayout, Statistic, LockIcon } from '../../components/StockTable';
import { TickerDisplayData } from '../../types/ticker';
import { tickerApi } from '../../services/tickerApi';
import { userTickerApi } from '../../services/userTickerApi';
import { userTickerListApi, UserTickerList } from '../../services/userTickerListApi';
import { userApi } from '../../services/userApi';
import TabBar from '../../components/Common/TabBar';
import {
  INSIDER_TRANSACTIONS_STATISTICS,
  INSIDER_TRANSACTIONS_CATEGORY_HEADERS,
  isEtfOrEtvMarketType,
  createEmptyInsiderTransactionData,
} from '../../utils/insiderTransactionsTabUtils';
import Footer from '../../components/Footer';
import Header from '../../components/Header/Header';
import PublicListPromoBar from '../../components/PublicListPromoBar';
import { 
  getCachedCurrentListName,
  getCachedCurrentListId,
  clearListCache,
  cacheCurrentListName,
} from '../../utils/listCache';
import { useUserListManagement } from '../../hooks/useUserListManagement';
import { useListPreferences } from '../../hooks/useListPreferences';
import { useAddTicker } from '../../hooks/useAddTicker';
import { useEditMode } from '../../hooks/useEditMode';
import { useDragAndDrop } from '../../hooks/useDragAndDrop';
import { useModals } from '../../hooks/useModals';
import { publicListApi, PublicListTickerMapping, PublicListPreferences } from '../../services/publicListApi';
import { usageLimitsApi } from '../../services/usageLimitsApi';
import CategoryInfoModal from '../../components/CategoryInfoModal/CategoryInfoModal';
import ShareListModal from '../../components/ShareListModal';
import DeleteListConfirmationModal from '../../components/Modals/DeleteListConfirmationModal';
import ErrorDialog from '../../components/Modals/ErrorDialog';

interface InsiderTransactionData {
  tickerSymbol: string;
  tickerBasicsId: number;
  aggregatedData: {
    numberOfBuyers: number;
    numberOfSellers: number;
    buyersMinusSellers: number;
    numberOfPurchases: number;
    numberOfSales: number;
    purchasesMinusSales: number;
    totalSharesPurchased: number;
    totalSharesSold: number;
    sharesPurchasedMinusSharesSold: number;
  };
  detailedData: {
    recentTransactions: any[];
  };
}

const ListInsiders: React.FC = () => {
  useEffect(() => {
    document.title = 'Stock Table - Insiders';
  }, []);

  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user } = useAuth();
  const { fontSize } = useFontSize();

  const searchParams = new URLSearchParams(location.search);
  const publicUuid = searchParams.get('public_uuid');
  const isPublicList = !!publicUuid;

  const [publicListData, setPublicListData] = useState<{
    publicListName: string | null;
    tickerMappings: PublicListTickerMapping[];
    listPreferences: PublicListPreferences | null;
    ownerHasProSubscription: boolean;
  } | null>(null);
  
  const PRO_ONLY_STATISTICS: string[] = [];
  
  // Determine locked statistics based on pro subscription status
  // For public lists, use owner's subscription status instead of viewer's
  const lockedStatistics = React.useMemo(() => {
    if (isPublicList && publicListData) {
      return publicListData.ownerHasProSubscription ? [] : PRO_ONLY_STATISTICS;
    }
    return (!isAuthenticated || !user?.hasProSubscription) ? PRO_ONLY_STATISTICS : [];
  }, [isPublicList, publicListData, isAuthenticated, user?.hasProSubscription]);
  const [isLoadingPublicList, setIsLoadingPublicList] = useState(false);
  const [publicListError, setPublicListError] = useState<string | null>(null);
  const [isPublicListNotFound, setIsPublicListNotFound] = useState(false);

  const [tickerList, setTickerList] = useState<string[]>(() => {
    if (isPublicList) return [];
    if (isAuthenticated) return [];
    const savedTickers = sessionStorage.getItem('stocktable-ticker-list');
    return savedTickers ? JSON.parse(savedTickers) : [];
  });

  const [listName, setListName] = useState<string>(() => {
    if (isPublicList && publicUuid) {
      const cached = sessionStorage.getItem(`public-list-name-${publicUuid}`);
      return cached || '';
    }
    if (isAuthenticated) {
      const cached = getCachedCurrentListName();
      if (cached) return cached;
    }
    const saved = sessionStorage.getItem('stocktable-list-name');
    return saved || 'Main List';
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Category info modal state
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  
  // Share modal state
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [insiderData, setInsiderData] = useState<Record<string, InsiderTransactionData>>({});
  const [tickerNames, setTickerNames] = useState<Record<string, string>>({});
  const [tickerBasicsIds, setTickerBasicsIds] = useState<Record<string, number>>({});
  
  // Initialize and sync ref with state
  useEffect(() => {
    tickerBasicsIdsRef.current = { ...tickerBasicsIds };
  }, [tickerBasicsIds]);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [syncingTickers, setSyncingTickers] = useState<Set<string>>(new Set());
  const [unsyncedTickers, setUnsyncedTickers] = useState<Set<string>>(new Set());
  const [pollingLimitReached, setPollingLimitReached] = useState<Set<string>>(new Set());

  const pollingStoppedRef = useRef(false);
  const syncPollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isCheckingSyncRef = useRef(false);
  const currentAttemptRef = useRef(1);
  const lastFetchedTickerListRef = useRef<string>('');
  const tickerBasicsIdsRef = useRef<Record<string, number>>({});
  /** Populated via getTickerBasics (and merged in fetchTickerBasics) for ETF/ETV short-circuit. */
  const marketTypeBySymbolRef = useRef<Record<string, string>>({});
  const publicListFetchAttemptedRef = useRef<string | null>(null);
  
  // Store original session storage values to restore when leaving public list
  const originalSessionStorageRef = useRef<{
    tickerList: string | null;
    listName: string | null;
    hiddenStatistics: string | null;
  } | null>(null);

  // Ref for refresh usage limits callback (used by useModals and useUserListManagement)
  const refreshUsageLimitsRef = useRef<(() => void) | null>(null);

  const {
    userLists,
    currentListId,
    setUserLists,
    handleListChange,
    handleCreateNewList,
    handleCreateNewListWithName,
  } = useUserListManagement({
    isAuthenticated: isAuthenticated && !isPublicList,
    tickerList,
    listName,
    setTickerList,
    setListName,
    refreshUsageLimitsRef,
    returnPath: '/insiders',
  });

  const {
    hiddenStatistics,
    setHiddenStatistics,
    handleHideStatistic,
    handleShowStatistic,
    savePreferences,
  } = useListPreferences({
    isAuthenticated: isAuthenticated && !isPublicList,
    currentListId: isPublicList ? null : currentListId,
    pageType: 'insiders',
    persistToSessionStorageImmediately: true,
  });

  // Fetch public list data when publicUuid is present
  useEffect(() => {
    // Only fetch if we haven't already attempted for this UUID, or if it's a different UUID
    const shouldFetch = publicUuid && 
                       !publicListData && 
                       !isLoadingPublicList && 
                       publicListFetchAttemptedRef.current !== publicUuid;
    
    if (shouldFetch) {
      // Save original session storage values before loading public list (only for unauthenticated users)
      if (!isAuthenticated && !originalSessionStorageRef.current) {
        originalSessionStorageRef.current = {
          tickerList: sessionStorage.getItem('stocktable-ticker-list'),
          listName: sessionStorage.getItem('stocktable-list-name'),
          hiddenStatistics: sessionStorage.getItem('stocktable-insiders-hidden-statistics'),
        };
      }

      // Mark that we're attempting to fetch this UUID
      publicListFetchAttemptedRef.current = publicUuid;
      setIsLoadingPublicList(true);
      setPublicListError(null);
      
      publicListApi.getPublicList(publicUuid)
        .then((response) => {
          setPublicListData({
            publicListName: response.data.public_list_name,
            tickerMappings: response.data.ticker_mappings,
            listPreferences: response.data.list_preferences,
            ownerHasProSubscription: response.data.owner_has_pro_subscription,
          });
          
          // Update ticker list from mappings
          const tickers = response.data.ticker_mappings
            .sort((a, b) => a.order_num - b.order_num)
            .map(mapping => mapping.ticker_symbol);
          setTickerList(tickers);
          
          // Clear the fetch key and insider data so data will be re-fetched with the correct ownerHasProSubscription
          // This ensures we get fresh data with the correct pro status
          lastFetchedTickerListRef.current = '';
          setInsiderData({}); // Clear existing data to force refetch
          
          // Update list name and cache it for tab switching
          const publicListName = response.data.public_list_name || 'Public List';
          setListName(publicListName);
          // Cache the public list name in sessionStorage for immediate restoration on tab switch
          if (publicUuid) {
            sessionStorage.setItem(`public-list-name-${publicUuid}`, publicListName);
          }
        })
        .catch((error) => {
          console.error('Failed to fetch public list:', error);
          const errorMessage = error.message || 'Failed to load public list';
          setPublicListError(errorMessage);
          
          // For 404 or 403 errors, set flag to show not found/not shared message instead of table
          if ((error as any).status === 404 || errorMessage.includes('not found')) {
            setIsPublicListNotFound(true);
            setTickerList([]);
            setListName('Public List');
            setPublicListError('This list does not exist or is no longer available.');
          } else if ((error as any).status === 403 || errorMessage.includes('not publicly shared')) {
            setIsPublicListNotFound(true);
            setTickerList([]);
            setListName('Public List');
            setPublicListError('This list is not publicly shared.');
          }
        })
        .finally(() => {
          setIsLoadingPublicList(false);
        });
    }
    
    // Reset the attempted ref and not found flag when publicUuid changes or becomes null
    if (!publicUuid) {
      publicListFetchAttemptedRef.current = null;
      setIsPublicListNotFound(false);
      setPublicListData(null);
      setPublicListError(null);
      
      // Restore original session storage values for unauthenticated users
      if (!isAuthenticated && originalSessionStorageRef.current) {
        if (originalSessionStorageRef.current.tickerList !== null) {
          sessionStorage.setItem('stocktable-ticker-list', originalSessionStorageRef.current.tickerList);
        } else {
          sessionStorage.removeItem('stocktable-ticker-list');
        }
        if (originalSessionStorageRef.current.listName !== null) {
          sessionStorage.setItem('stocktable-list-name', originalSessionStorageRef.current.listName);
        } else {
          sessionStorage.removeItem('stocktable-list-name');
        }
        if (originalSessionStorageRef.current.hiddenStatistics !== null) {
          sessionStorage.setItem('stocktable-insiders-hidden-statistics', originalSessionStorageRef.current.hiddenStatistics);
        } else {
          sessionStorage.removeItem('stocktable-insiders-hidden-statistics');
        }
        originalSessionStorageRef.current = null;
      }
    }
  }, [publicUuid, publicListData, isLoadingPublicList, isAuthenticated]);

  // Edit mode state
  const [isEditingTickers, setIsEditingTickers] = useState(false);

  // Fetch ticker basics to get ticker_basics_ids
  // For authenticated users with a list, we can get IDs from the list response
  // For unauthenticated users or public lists, we need to call the API
  const fetchTickerBasics = useCallback(async (tickers: string[]) => {
    if (tickers.length === 0) return {};
    
    // Check if we already have all the IDs we need
    const missingTickers = tickers.filter(t => !tickerBasicsIdsRef.current[t]);
    if (missingTickers.length === 0) {
      // Return cached IDs
      const cachedMap: Record<string, number> = {};
      tickers.forEach(t => {
        if (tickerBasicsIdsRef.current[t]) {
          cachedMap[t] = tickerBasicsIdsRef.current[t];
        }
      });
      return cachedMap;
    }
    
    // For authenticated users with a current list, try to get IDs from list data
    if (isAuthenticated && currentListId && !isPublicList) {
      try {
        const listWithTickers = await userTickerListApi.getListWithTickers(currentListId);
        const basicsMap: Record<string, number> = {};
        
        listWithTickers.tickers.forEach((t: any) => {
          // The 'id' field from the list response IS the ticker_basics_id
          if (t.ticker_symbol && t.id) {
            basicsMap[t.ticker_symbol] = t.id;
            tickerBasicsIdsRef.current[t.ticker_symbol] = t.id;
          }
        });
        
        // Update state for any new IDs
        const newIds = Object.keys(basicsMap).filter(k => !tickerBasicsIdsRef.current[k]);
        if (newIds.length > 0) {
          setTickerBasicsIds(prev => ({ ...prev, ...basicsMap }));
        }
        
        // Check if we got all the tickers we need
        const allFound = missingTickers.every(t => basicsMap[t]);
        if (allFound && Object.keys(basicsMap).length > 0) {
          return basicsMap;
        }
      } catch (err) {
        console.warn('Could not get ticker basics from list, falling back to API:', err);
      }
    }
    
    // Fallback: Call the API (for unauthenticated users, public lists, or if list fetch failed)
    try {
      const api = isAuthenticated ? userTickerApi : tickerApi;
      const response = await api.getTickerBasics(missingTickers);
      if (response.success && response.data) {
        const basicsMap: Record<string, number> = {};
        const namesMap: Record<string, string> = {};
        response.data.forEach((tb: any) => {
          basicsMap[tb.tickerSymbol] = tb.id;
          tickerBasicsIdsRef.current[tb.tickerSymbol] = tb.id;
          if (tb.tickerName) namesMap[tb.tickerSymbol] = tb.tickerName;
          if (tb.marketType != null) {
            marketTypeBySymbolRef.current[tb.tickerSymbol] = tb.marketType;
          }
        });
        setTickerBasicsIds(prev => ({ ...prev, ...basicsMap }));
        setTickerNames(prev => ({ ...prev, ...namesMap }));
        return basicsMap;
      }
    } catch (err) {
      console.error('Error fetching ticker basics:', err);
    }
    return {};
  }, [isAuthenticated, currentListId, isPublicList]);

  /** Ensures marketTypeBySymbolRef is filled so ETF/ETV tickers skip insider sync (list API alone may not include market type). */
  const loadMarketTypesForSymbols = useCallback(async (symbols: string[]) => {
    const need = symbols.filter(s => marketTypeBySymbolRef.current[s] === undefined);
    if (need.length === 0) return;
    try {
      const api = isAuthenticated ? userTickerApi : tickerApi;
      const response = await api.getTickerBasics(need);
      if (response.success && response.data) {
        response.data.forEach((tb: any) => {
          marketTypeBySymbolRef.current[tb.tickerSymbol] =
            tb.marketType != null ? String(tb.marketType) : '';
        });
      }
    } catch (err) {
      console.warn('ListInsiders: could not load market types for ETF/ETV insider handling:', err);
    }
  }, [isAuthenticated]);

  // Check insider sync status and fetch data
  const checkInsiderSyncAndFetchData = useCallback(async (tickersToCheck: string[], attemptNumber: number = 1) => {
    if (tickersToCheck.length === 0) return;
    if (attemptNumber > 10) {
      setPollingLimitReached(prev => {
        const newSet = new Set(prev);
        tickersToCheck.forEach(t => newSet.add(t));
        return newSet;
      });
      setUnsyncedTickers(prev => {
        const newSet = new Set(prev);
        tickersToCheck.forEach(t => newSet.delete(t));
        return newSet;
      });
      return;
    }

    if (pollingStoppedRef.current || isCheckingSyncRef.current) return;
    isCheckingSyncRef.current = true;

    try {
      // Get ticker basics IDs
      const basicsMap = await fetchTickerBasics(tickersToCheck);
      await loadMarketTypesForSymbols(tickersToCheck);

      const etfSymbols = tickersToCheck.filter(t => isEtfOrEtvMarketType(marketTypeBySymbolRef.current[t]));
      if (etfSymbols.length > 0) {
        setUnsyncedTickers(prev => {
          const next = new Set(prev);
          etfSymbols.forEach(t => next.delete(t));
          return next;
        });
        setSyncingTickers(prev => {
          const next = new Set(prev);
          etfSymbols.forEach(t => next.delete(t));
          return next;
        });
        setInsiderData(prev => {
          const next = { ...prev };
          etfSymbols.forEach(sym => {
            const id = basicsMap[sym] ?? tickerBasicsIdsRef.current[sym];
            if (id != null) {
              next[sym] = createEmptyInsiderTransactionData(sym, id);
            }
          });
          return next;
        });
      }

      const nonEtfTickers = tickersToCheck.filter(t => !etfSymbols.includes(t));
      const tickerBasicsIdsArray = nonEtfTickers
        .map(t => basicsMap[t] || tickerBasicsIdsRef.current[t])
        .filter((id): id is number => id !== undefined);

      if (tickerBasicsIdsArray.length === 0) {
        // No non-ETF rows with ticker_basics ids yet — or only ETFs (handled above)
        if (nonEtfTickers.length === 0) {
          isCheckingSyncRef.current = false;
          return;
        }
        // Tickers not in ticker_basics yet (e.g. invalid or not yet created) — show as syncing and poll
        setUnsyncedTickers(prev => {
          const next = new Set(prev);
          nonEtfTickers.forEach(t => next.add(t));
          return next;
        });
        setSyncingTickers(prev => {
          const next = new Set(prev);
          nonEtfTickers.forEach(t => next.delete(t));
          return next;
        });
        if (attemptNumber >= 10) {
          isCheckingSyncRef.current = false;
          return;
        }
        if (pollingStoppedRef.current) {
          isCheckingSyncRef.current = false;
          return;
        }
        const pollingDelay = 5000;
        if (syncPollingIntervalRef.current) {
          clearInterval(syncPollingIntervalRef.current);
        }
        currentAttemptRef.current = attemptNumber;
        syncPollingIntervalRef.current = setInterval(() => {
          if (pollingStoppedRef.current) {
            if (syncPollingIntervalRef.current) {
              clearInterval(syncPollingIntervalRef.current);
              syncPollingIntervalRef.current = null;
            }
            isCheckingSyncRef.current = false;
            return;
          }
          const nextAttempt = currentAttemptRef.current + 1;
          if (nextAttempt > 10) {
            if (syncPollingIntervalRef.current) {
              clearInterval(syncPollingIntervalRef.current);
              syncPollingIntervalRef.current = null;
            }
            pollingStoppedRef.current = true;
            isCheckingSyncRef.current = false;
            return;
          }
          currentAttemptRef.current = nextAttempt;
          checkInsiderSyncAndFetchData([...nonEtfTickers], nextAttempt);
        }, pollingDelay);
        isCheckingSyncRef.current = false;
        return;
      }

      const api = isAuthenticated ? userTickerApi : tickerApi;
      const syncResponse = await api.checkInsiderSyncStatus({
        ticker_basics_ids: tickerBasicsIdsArray,
      });

      if (syncResponse.success && syncResponse.data) {
        const syncedTickers: string[] = [];
        const stillUnsynced: string[] = [];
        const tickerIdToSymbol: Record<number, string> = {};

        nonEtfTickers.forEach(ticker => {
          const id = basicsMap[ticker] || tickerBasicsIdsRef.current[ticker];
          if (id) tickerIdToSymbol[id] = ticker;
        });

        syncResponse.data.tickers.forEach((tickerStatus: any) => {
          const symbol = tickerIdToSymbol[tickerStatus.ticker_basics_id];
          if (!symbol) return;

          if (tickerStatus.is_synced) {
            syncedTickers.push(symbol);
            if (tickerStatus.ticker_name) {
              setTickerNames(prev => ({ ...prev, [symbol]: tickerStatus.ticker_name }));
            }
          } else {
            stillUnsynced.push(symbol);
          }
        });

        // Fetch data for synced tickers
        if (syncedTickers.length > 0) {
          const syncedIds = syncedTickers
            .map(t => basicsMap[t] || tickerBasicsIdsRef.current[t])
            .filter(id => id !== undefined);

          const today = new Date();
          const startDate = new Date(today);
          startDate.setDate(startDate.getDate() - 90);
          const endDateStr = today.toISOString().split('T')[0];
          const startDateStr = startDate.toISOString().split('T')[0];

          try {
            const transactionsResponse = await api.getInsiderTransactionsByDateRange({
              ownerHasProSubscription: isPublicList && publicListData ? publicListData.ownerHasProSubscription : undefined,
              tickerBasicsIds: syncedIds,
              startDate: startDateStr,
              endDate: endDateStr,
            });

            if (transactionsResponse.success && transactionsResponse.data) {
              const newData: Record<string, InsiderTransactionData> = {};
              transactionsResponse.data.forEach((item: any) => {
                newData[item.tickerSymbol] = item;
              });
              setInsiderData(prev => ({ ...prev, ...newData }));

              // Safety: data loaded => clear syncing/unsynced flags so UI can't remain pulsing.
              setSyncingTickers(prev => {
                const next = new Set(prev);
                syncedTickers.forEach((t: string) => next.delete(t));
                return next;
              });
              setUnsyncedTickers(prev => {
                const next = new Set(prev);
                syncedTickers.forEach((t: string) => next.delete(t));
                return next;
              });

              // Update last updated
              const syncedWithDate = syncResponse.data.tickers.filter((t: any) => t.is_synced && t.last_sync_ended_at);
              if (syncedWithDate.length > 0) {
                const dates = syncedWithDate
                  .map((t: any) => new Date(t.last_sync_ended_at))
                  .filter((d: Date) => !isNaN(d.getTime()));
                if (dates.length > 0) {
                  setLastUpdated(new Date(Math.max(...dates.map((d: Date) => d.getTime()))));
                }
              }
            }
          } catch (err) {
            console.error('Error fetching insider transactions:', err);
          }
        }

        // Update unsynced tickers
        setUnsyncedTickers(prev => {
          const newSet = new Set(prev);
          syncedTickers.forEach(t => newSet.delete(t));
          stillUnsynced.forEach(t => newSet.add(t));
          return newSet;
        });
        setSyncingTickers(prev => {
          const newSet = new Set(prev);
          syncedTickers.forEach(t => newSet.delete(t));
          return newSet;
        });

        // Continue polling for unsynced tickers
        if (stillUnsynced.length > 0 && attemptNumber < 10) {
          if (pollingStoppedRef.current) {
            isCheckingSyncRef.current = false;
            return;
          }

          const pollingDelay = 5000;
          if (syncPollingIntervalRef.current) {
            clearInterval(syncPollingIntervalRef.current);
          }

          syncPollingIntervalRef.current = setInterval(() => {
            if (pollingStoppedRef.current) {
              if (syncPollingIntervalRef.current) {
                clearInterval(syncPollingIntervalRef.current);
                syncPollingIntervalRef.current = null;
              }
              isCheckingSyncRef.current = false;
              return;
            }

            const nextAttempt = currentAttemptRef.current + 1;
            if (nextAttempt > 10) {
              if (syncPollingIntervalRef.current) {
                clearInterval(syncPollingIntervalRef.current);
                syncPollingIntervalRef.current = null;
              }
              pollingStoppedRef.current = true;
              isCheckingSyncRef.current = false;
              return;
            }

            currentAttemptRef.current = nextAttempt;
            setUnsyncedTickers(currentUnsynced => {
              if (currentUnsynced.size > 0) {
                checkInsiderSyncAndFetchData(Array.from(currentUnsynced), nextAttempt);
              }
              return currentUnsynced;
            });
          }, pollingDelay);
        }
      }
    } catch (err) {
      console.error('Error checking insider sync status:', err);
    } finally {
      isCheckingSyncRef.current = false;
    }
  }, [isAuthenticated, fetchTickerBasics, loadMarketTypesForSymbols, isPublicList, publicListData]);

  // Initial data fetch
  useEffect(() => {
    // For public lists, wait for publicListData to be loaded before fetching
    if (isPublicList && !publicListData) {
      return; // Wait for publicListData to load
    }

    if (tickerList.length === 0) {
      lastFetchedTickerListRef.current = '';
      return;
    }

    // Create a stable key for the current ticker list to prevent re-fetching
    // Use sorted version for comparison only (don't mutate original array)
    // Include ownerHasProSubscription in the key so we re-fetch when it changes
    const ownerProStatus = isPublicList && publicListData ? String(publicListData.ownerHasProSubscription) : '';
    const tickerListKey = [...tickerList].sort().join(',') + (ownerProStatus ? `|pro:${ownerProStatus}` : '');
    
    // Only fetch if this is a different ticker list or pro status than last time
    if (lastFetchedTickerListRef.current === tickerListKey) {
      return;
    }

    const fetchData = async () => {
      // Mark that we're fetching for this ticker list
      const previousKey = lastFetchedTickerListRef.current;
      lastFetchedTickerListRef.current = tickerListKey;
      
      // Determine which tickers are new (not in the previous list)
      // Extract just the ticker list part (before the |pro: part if it exists)
      const previousKeyTickers = previousKey ? previousKey.split('|')[0].split(',').filter(t => t) : [];
      const newTickers = tickerList.filter(t => !previousKeyTickers.includes(t));
      
      setLoading(true);
      pollingStoppedRef.current = false;
      currentAttemptRef.current = 1;
      isCheckingSyncRef.current = false;

      if (syncPollingIntervalRef.current) {
        clearInterval(syncPollingIntervalRef.current);
        syncPollingIntervalRef.current = null;
      }

      // Only mark NEW tickers as syncing/unsynced, preserve state for existing tickers
      // This prevents existing data from being cleared when a new ticker is added
      if (newTickers.length > 0) {
        setSyncingTickers(prev => {
          const newSet = new Set(prev);
          newTickers.forEach(ticker => newSet.add(ticker));
          return newSet;
        });
        setUnsyncedTickers(prev => {
          const newSet = new Set(prev);
          newTickers.forEach(ticker => newSet.add(ticker));
          return newSet;
        });
      }

      // On ticker additions, only sync/fetch for the new tickers.
      // (Avoid re-checking + re-fetching insider data for the whole list, which can cause all columns to pulse.)
      const tickersToFetch = newTickers.length > 0 ? newTickers : tickerList;
      await checkInsiderSyncAndFetchData(tickersToFetch, 1);
      setLoading(false);
    };

    fetchData();
  }, [tickerList, checkInsiderSyncAndFetchData, isPublicList, publicListData]);

  // Transform data for display
  const transformDataForDisplay = useCallback((data: Record<string, InsiderTransactionData>): TickerDisplayData[] => {
    return tickerList.map(ticker => {
      const tickerData = data[ticker];
      const isSyncing = syncingTickers.has(ticker) || unsyncedTickers.has(ticker);
      const tickerName = tickerNames[ticker] || ticker;

      if (!tickerData || isSyncing) {
        return {
          ticker,
          name: ticker,
          ticker_name: tickerName,
          ticker_symbol: ticker,
          latest: 0,
          change: 0,
          changePercent: 0,
          volume: 0,
          high: 0,
          low: 0,
          open: 0,
          prevClose: 0,
          updated: Date.now(),
          isSyncing: true,
        } as TickerDisplayData;
      }

      const result: TickerDisplayData = {
        ticker,
        name: ticker,
        ticker_name: tickerName,
        ticker_symbol: ticker,
        latest: 0,
        change: 0,
        changePercent: 0,
        volume: 0,
        high: 0,
        low: 0,
        open: 0,
        prevClose: 0,
        updated: Date.now(),
        isSyncing: false,
        // Aggregated data
        numberOfBuyers: tickerData.aggregatedData.numberOfBuyers,
        numberOfSellers: tickerData.aggregatedData.numberOfSellers,
        buyersMinusSellers: tickerData.aggregatedData.buyersMinusSellers,
        numberOfPurchases: tickerData.aggregatedData.numberOfPurchases,
        numberOfSales: tickerData.aggregatedData.numberOfSales,
        purchasesMinusSales: tickerData.aggregatedData.purchasesMinusSales,
        totalSharesPurchased: tickerData.aggregatedData.totalSharesPurchased,
        totalSharesSold: tickerData.aggregatedData.totalSharesSold,
        sharesPurchasedMinusSharesSold: tickerData.aggregatedData.sharesPurchasedMinusSharesSold,
      } as any;

      // Recent transactions - store full transaction objects
      // Handle case where recentTransactions might be undefined (for cached data without this field)
      if (tickerData.detailedData?.recentTransactions) {
        tickerData.detailedData.recentTransactions.forEach((transaction, idx) => {
          (result as any)[`recentTransaction${idx + 1}`] = transaction || null;
        });
      } else {
        // Initialize all recentTransaction fields to null if data is not available
        for (let idx = 0; idx < 10; idx++) {
          (result as any)[`recentTransaction${idx + 1}`] = null;
        }
      }

      return result;
    });
  }, [tickerList, syncingTickers, unsyncedTickers, tickerNames, insiderData]);

  // Format statistic value
  const formatStatisticValue = useCallback((statisticKey: string, ticker: TickerDisplayData): React.ReactNode => {
    if (ticker.isSyncing) {
      return <div className="animate-pulse bg-gray-200 dark:bg-border-color h-4 w-16 rounded" title="Syncing..." />;
    }
    const value = (ticker as any)[statisticKey];
    
    // Check if this statistic is locked for non-pro users
    // Only show lock icon if the statistic is locked AND value is null
    // If statistic is not locked but value is null, it means there's no data (show "-")
    if (lockedStatistics.includes(statisticKey)) {
      // If value is null and statistic is locked, show lock icon
      if (value === null || value === undefined) {
        return <LockIcon className="w-4 h-4 text-gray-500 dark:text-gray-400" onClick={!isPublicList ? () => navigate('/pricing', { state: { fromUpgrade: true } }) : undefined} tooltipText={isPublicList ? "Locked due to the list owner's plan" : "Upgrade to the Pro Plan to see"} />;
      }
    }
    
    // If value is null but statistic is not locked, it means there's no data
    if (value === null || value === undefined) return <span className="text-gray-400 dark:text-text-secondary">-</span>;
    
    // Check if this is a transaction detail statistic (lastPurchase, lastSale, topPurchase, topSale, recentTransaction)
    const isTransactionDetail = statisticKey.startsWith('lastPurchase') || 
                                 statisticKey.startsWith('lastSale') || 
                                 statisticKey.startsWith('topPurchase') || 
                                 statisticKey.startsWith('topSale') ||
                                 statisticKey.startsWith('recentTransaction');
    
    if (isTransactionDetail && typeof value === 'object' && value !== null) {
      // Format transaction details as multi-line text with reduced font size
      const transaction = value as any;
      const dateStr = transaction.transactionDate 
        ? new Date(transaction.transactionDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
        : '-';
      const nameStr = transaction.name || '-';
      const positionStr = transaction.position || '-';
      
      // For recentTransaction, use Bought/Sold format
      if (statisticKey.startsWith('recentTransaction')) {
        const isPurchase = transaction.transactionCode && transaction.transactionCode.toUpperCase().startsWith('P');
        const sharesStr = transaction.totalShares 
          ? transaction.totalShares.toLocaleString('en-US', { maximumFractionDigits: 0 })
          : '-';
        const priceStr = transaction.weightedPrice !== null && transaction.weightedPrice !== undefined
          ? `$${transaction.weightedPrice.toFixed(2)}`
          : '';
        
        // Format transacted shares percentage
        // transactedSharesPercentage is already stored as a percentage (e.g., 5.0 for 5%)
        let percentageStr = '';
        if (transaction.transactedSharesPercentage !== null && transaction.transactedSharesPercentage !== undefined) {
          // Round to 2 decimal places (nearest hundredth)
          const percentage = Math.round(transaction.transactedSharesPercentage * 100) / 100;
          percentageStr = ` (${percentage.toFixed(2)}% of holdings)`;
        }
        
        const actionStr = isPurchase 
          ? (priceStr ? `Bought ${sharesStr} at ${priceStr}${percentageStr}` : `Bought ${sharesStr}${percentageStr}`)
          : (priceStr ? `Sold ${sharesStr} at ${priceStr}${percentageStr}` : `Sold ${sharesStr}${percentageStr}`);
        
        // Color purchase transactions green, sales remain default (black)
        const textColorClass = isPurchase ? 'text-green-light dark:text-green-500' : '';
        
        return (
          <div className={`${getTightCellTextSizeClass(fontSize)} leading-tight ${textColorClass}`}>
            <div>{dateStr}</div>
            <div>{nameStr}</div>
            <div>{positionStr}</div>
            <div>{actionStr}</div>
          </div>
        );
      } else {
        // For other transaction details, use the original format
        const sharesStr = transaction.totalShares 
          ? transaction.totalShares.toLocaleString('en-US', { maximumFractionDigits: 0 })
          : '-';
        const priceStr = transaction.weightedPrice !== null && transaction.weightedPrice !== undefined
          ? `($${transaction.weightedPrice.toFixed(2)})`
          : '';
        
        return (
          <div className={`${getTightCellTextSizeClass(fontSize)} leading-tight`}>
            <div>{dateStr}</div>
            <div>{nameStr}</div>
            <div>{positionStr}</div>
            <div>{sharesStr} {priceStr}</div>
          </div>
        );
      }
    }
    
    if (typeof value === 'number') {
      // Format all numeric values with locale string (no decimals for counts/shares)
      return value.toLocaleString('en-US', { maximumFractionDigits: 0 });
    }
    return String(value);
  }, [lockedStatistics, isPublicList, navigate, fontSize]);

  // Get statistic color
  const getStatisticColor = useCallback((statisticKey: string, ticker: TickerDisplayData): string => {
    if (ticker.isSyncing) return 'text-text-light-neutral dark:text-text-secondary';
    
    const value = (ticker as any)[statisticKey];
    if (value === null || value === undefined) {
      return 'text-text-light-neutral dark:text-text-secondary';
    }
    
    // For difference statistics (buyers - sellers, purchases - sales, etc.)
    if (statisticKey === 'buyersMinusSellers' || 
        statisticKey === 'purchasesMinusSales' || 
        statisticKey === 'sharesPurchasedMinusSharesSold') {
      if (typeof value === 'number') {
        if (value > 0) {
          return 'text-green-light dark:text-green-500'; // Positive (more buyers/purchases)
        } else if (value < 0) {
          return 'text-red-light dark:text-red-500'; // Negative (more sellers/sales)
        }
      }
      return 'text-text-light-neutral dark:text-text-secondary'; // Zero or neutral
    }
    
    // For count statistics, neutral color
    if (statisticKey === 'numberOfBuyers' || 
        statisticKey === 'numberOfSellers' || 
        statisticKey === 'numberOfPurchases' || 
        statisticKey === 'numberOfSales') {
      return 'text-text-light-neutral dark:text-text-secondary';
    }
    
    // For total shares, neutral color
    if (statisticKey === 'totalSharesPurchased' || 
        statisticKey === 'totalSharesSold') {
      return 'text-text-light-neutral dark:text-text-secondary';
    }
    
    // For last/top purchases and sales, and recent transactions, neutral color (they're just showing transaction details)
    if (statisticKey.startsWith('lastPurchase') || 
        statisticKey.startsWith('lastSale') || 
        statisticKey.startsWith('topPurchase') || 
        statisticKey.startsWith('topSale') ||
        statisticKey.startsWith('recentTransaction')) {
      return 'text-text-light-neutral dark:text-text-secondary';
    }
    
    // Default neutral color
    return 'text-text-light-neutral dark:text-text-secondary';
  }, []);

  // Get ticker data
  const getTickerData = useCallback((symbol: string): TickerDisplayData | undefined => {
    const data = transformDataForDisplay(insiderData);
    return data.find(d => d.ticker === symbol);
  }, [insiderData, transformDataForDisplay]);

  // Helper function to get sessionStorage key based on list ID
  const getTickerListKey = useCallback(() => {
    return isAuthenticated && currentListId !== null 
      ? `stocktable-ticker-list-${currentListId}` 
      : 'stocktable-ticker-list';
  }, [isAuthenticated, currentListId]);

  // Auto-save function for authenticated users (needed for handleSaveChanges)
  const autoSaveList = useCallback(async (tickers: string[]) => {
    // Don't auto-save in public list mode
    if (isPublicList || !isAuthenticated) return;
    
    // Determine which list ID to use
    let listId = currentListId;
    
    // If currentListId is null, try to get it from userLists or wait for it
    if (listId === null) {
      // First, try to get from cache (might be set by createDefaultMainListIfNeeded)
      const { getCachedCurrentListId } = await import('../../utils/listCache');
      const cachedListId = getCachedCurrentListId();
      if (cachedListId) {
        console.log('💾 Using cached list ID for auto-save:', cachedListId);
        listId = cachedListId;
      } else if (userLists.length > 0) {
        // If userLists has items, use the first list (or try to get default list)
        // Try to get the user's default list
        try {
          const defaultListResponse = await userApi.getDefaultList();
          if (defaultListResponse.defaultUserTickerListId) {
            const defaultListExists = userLists.some(list => list.id === defaultListResponse.defaultUserTickerListId);
            if (defaultListExists) {
              listId = defaultListResponse.defaultUserTickerListId;
            } else {
              // Default list doesn't exist in userLists, use first list
              listId = userLists[0].id;
            }
          } else {
            // No default list set, use first list
            listId = userLists[0].id;
          }
        } catch (error) {
          // If we can't get default list, use first list
          console.log('⚠️ Could not get default list, using first list:', error);
          listId = userLists[0].id;
        }
      } else {
        // No lists available yet - ticker is saved to sessionStorage and will be recovered when lists load
        console.log('⚠️ Auto-save skipped: currentListId is null and no userLists available. Ticker saved to sessionStorage.');
        return;
      }
    }
    
    // Ensure listId is not null before proceeding
    if (!listId) {
      console.log('⚠️ Auto-save skipped: No list ID available');
      return;
    }
    
    try {
      const tickerMappings = tickers.map((ticker, index) => ({
        ticker_symbol: ticker,
        order_num: index + 1
      }));
      
      await userTickerListApi.updateTickerMappings(listId, tickerMappings);
      
      // Clear the cache for this list to force reload on tab switch
      clearListCache(listId);
    } catch (error) {
      console.error('Auto-save failed:', error);
    }
  }, [isAuthenticated, currentListId, isPublicList, userLists]);

  // Handle save changes (used by edit mode hook)
  const handleSaveChanges = useCallback(async (saveTempTickerList: string[], saveTempListName: string) => {
    // Don't allow saving changes in public list mode
    if (isPublicList) {
      return;
    }
    
    const newTickerList = [...saveTempTickerList];

    if (!isAuthenticated) {
      sessionStorage.setItem(getTickerListKey(), JSON.stringify(newTickerList));
      sessionStorage.setItem('stocktable-ticker-list', JSON.stringify(newTickerList));
    }

    setTickerList(newTickerList);
    setListName(saveTempListName);
    setIsEditingTickers(false);
    
    // Auto-save for authenticated users
    if (isAuthenticated && currentListId) {
      try {
        // Update list name if it changed
        if (saveTempListName !== listName) {
          await userTickerListApi.updateListName(currentListId, saveTempListName);
          
          // Update the user lists cache to reflect the name change
          setUserLists((prev: UserTickerList[]) => prev.map((list: UserTickerList) => 
            list.id === currentListId 
              ? { ...list, list_name: saveTempListName }
              : list
          ));
          
          // Cache the updated list name to prevent flickering on tab switch
          cacheCurrentListName(saveTempListName);
        }
        
        // Update ticker mappings
        await autoSaveList(newTickerList);
        
        // Refresh usage limits after successful mappings update
        try {
          await Promise.all([
            usageLimitsApi.checkTickerLimit(),
            usageLimitsApi.checkListLimit()
          ]);
        } catch (error) {
          console.error('Failed to refresh usage limits:', error);
        }
        
        // Save user list preferences for insider indicators
        await savePreferences(hiddenStatistics);
      } catch (error) {
        console.error('Failed to update list:', error);
      }
    } else {
      // Save list name to session storage for unauthenticated users
      sessionStorage.setItem('stocktable-list-name', saveTempListName);
      // Save preferences to sessionStorage (for unauthenticated users)
      await savePreferences(hiddenStatistics);
    }
    
    // Note: sessionStorage is already updated above before state changes
    // This prevents the recovery mechanism from restoring removed tickers
  }, [tickerList, listName, isAuthenticated, currentListId, setTickerList, setListName, setIsEditingTickers, hiddenStatistics, setUserLists, autoSaveList, savePreferences, getTickerListKey]);

  // Edit mode hook
  const {
    tempTickerList,
    tempListName,
    setTempTickerList,
    setTempListName,
    handleStartEdit,
    handleCancelEdit,
    handleDoneEdit,
    handleSave,
  } = useEditMode({
    tickerList,
    listName,
    setTickerList,
    setListName,
    isEditingTickers,
    setIsEditingTickers,
    returnPath: '/insiders',
    onSaveChanges: handleSaveChanges,
    hiddenStatistics: undefined, // Insiders page doesn't use hiddenStatistics in useEditMode
  });

  // Drag and drop hook (uses 'insert' behavior by default)
  const {
    draggedTicker,
    dragOverTicker,
    droppedTicker,
    handleDragStart,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleDragEnd,
  } = useDragAndDrop({
    tempTickerList,
    setTempTickerList,
    isEditingTickers,
  });

  // Handle remove ticker
  const handleRemoveTicker = useCallback(async (tickerToRemove: string) => {
    if (isEditingTickers) {
      const newTempList = tempTickerList.filter(t => t !== tickerToRemove);
      setTempTickerList(newTempList);
      // No auto-save in edit mode - will be saved when "Done" is clicked
    } else {
      const newList = tickerList.filter(t => t !== tickerToRemove);
      setTickerList(newList);
      
      if (isAuthenticated) {
        await autoSaveList(newList);
      }
    }
  }, [isEditingTickers, tempTickerList, setTempTickerList, tickerList, setTickerList, isAuthenticated, autoSaveList]);

  const handleTickerClick = useCallback((tickerSymbol: string) => {
    const id = tickerBasicsIdsRef.current[tickerSymbol];
    const state = id != null ? { tickerBasicsId: id } : undefined;
    if (isPublicList && publicUuid) {
      navigate(`/ticker/${tickerSymbol}/insiders?public_uuid=${publicUuid}`, { state });
    } else {
      navigate(`/ticker/${tickerSymbol}/insiders`, { state });
    }
  }, [navigate, isPublicList, publicUuid]);

  const { handleAddTicker } = useAddTicker({
    isAuthenticated,
    tickerList,
    setTickerList,
    setSyncingTickers,
    tempTickerList: tempTickerList || [],
    setTempTickerList: setTempTickerList as React.Dispatch<React.SetStateAction<string[]>> | undefined,
    isEditingTickers,
    onTickerNameUpdate: useCallback((ticker: string, tickerName: string) => {
      if (tickerName) {
        setTickerNames(prev => ({ ...prev, [ticker]: tickerName }));
      }
    }, []),
    onSyncComplete: useCallback(async (ticker: string) => {
      setUnsyncedTickers(prev => {
        const newSet = new Set(prev);
        newSet.delete(ticker);
        return newSet;
      });
      await checkInsiderSyncAndFetchData([ticker], 1);
    }, [checkInsiderSyncAndFetchData]),
    autoSaveList,
    saveListImmediately: useCallback(async (tickers: string[]) => {
      const uniqueTickers = Array.from(new Set(tickers));
      if (isPublicList) return;

      if (isAuthenticated && !isPublicList) {
        let listId = currentListId;
        if (listId === null) {
          const cachedListId = getCachedCurrentListId();
          if (cachedListId) listId = cachedListId;
          else if (userLists.length > 0) {
            try {
              const defaultListResponse = await userApi.getDefaultList();
              if (defaultListResponse.defaultUserTickerListId && userLists.some((l: UserTickerList) => l.id === defaultListResponse.defaultUserTickerListId)) {
                listId = defaultListResponse.defaultUserTickerListId;
              } else {
                listId = userLists[0].id;
              }
            } catch {
              listId = userLists[0].id;
            }
          }
        }
        if (listId != null) {
          try {
            const tickerMappings = uniqueTickers.map((ticker, index) => ({ ticker_symbol: ticker, order_num: index + 1 }));
            await userTickerListApi.updateTickerMappings(listId, tickerMappings);
            clearListCache(listId);
          } catch (error) {
            console.error('saveListImmediately: failed to save to server', error);
          }
        }
        return;
      }

      sessionStorage.setItem('stocktable-ticker-list', JSON.stringify(uniqueTickers));
      if (currentListId !== null) {
        sessionStorage.setItem(`stocktable-ticker-list-${currentListId}`, JSON.stringify(uniqueTickers));
      }
    }, [currentListId, isPublicList, isAuthenticated, userLists]),
    pollingInterval: 2000,
    maxPollAttempts: 10,
    normalizeTicker: (ticker: string) => ticker.toUpperCase(),
    onError: useCallback((ticker: string) => {
      setPollingLimitReached(prev => {
        const newSet = new Set(prev);
        newSet.add(ticker);
        return newSet;
      });
      // Keep column in syncing state (grey pulsing) so it doesn't show "-" for missing ticker
      setUnsyncedTickers(prev => {
        const next = new Set(prev);
        next.add(ticker);
        return next;
      });
      // Retry so we poll until ticker appears in ticker_basics or attempt limit
      checkInsiderSyncAndFetchData([ticker], 1);
    }, [checkInsiderSyncAndFetchData]),
  });

  // Handle category info click
  const handleCategoryInfoClick = useCallback((category: string) => {
    setSelectedCategory(category);
    setIsCategoryModalOpen(true);
  }, []);

  // Handle category modal close
  const handleCloseCategoryModal = useCallback(() => {
    setIsCategoryModalOpen(false);
    setSelectedCategory('');
  }, []);

  // Sort user lists by name
  const sortedUserLists = useMemo(() => {
    if (!userLists) return [];
    return [...userLists].sort((a, b) => {
      const nameA = a.list_name || '';
      const nameB = b.list_name || '';
      return nameA.localeCompare(nameB);
    });
  }, [userLists]);

  // Calculate locked list IDs (lists beyond the 3rd position for non-Pro users)
  const lockedListIds = useMemo(() => {
    if (isAuthenticated && !user?.hasProSubscription && sortedUserLists.length > 3) {
      // Lists beyond the 3rd position (index 3+) are locked
      return sortedUserLists.slice(3).map(list => list.id);
    }
    return [];
  }, [sortedUserLists, isAuthenticated, user?.hasProSubscription]);

  // Check if current list is locked
  const isCurrentListLocked = useMemo(() => {
    return currentListId !== null && lockedListIds.includes(currentListId);
  }, [currentListId, lockedListIds]);

  // Get current list data for share modal
  const currentList = currentListId && userLists
    ? userLists.find(list => list.id === currentListId) || null
    : null;

  // Share modal handlers
  const handleShareList = useCallback(() => {
    setIsShareModalOpen(true);
  }, []);

  const handleShareModalClose = useCallback(() => {
    setIsShareModalOpen(false);
  }, []);

  const handleShareModalUpdate = useCallback(async () => {
    // Refresh user lists to get updated share settings
    if (isAuthenticated && currentListId) {
      try {
        const updatedLists = await userTickerListApi.getUserLists();
        setUserLists(updatedLists);
      } catch (error) {
        console.error('Failed to refresh lists after share update:', error);
      }
    }
  }, [isAuthenticated, currentListId, setUserLists]);

  // Ref to track if user lists need to be reloaded after delete
  const needsUserListsReload = useRef(false);

  // Modals hook (must be after handleListChange is defined)
  const {
    isDeleteModalOpen,
    isErrorDialogOpen,
    errorMessage,
    handleDeleteList,
    handleDeleteCancel,
    handleDeleteConfirm,
    handleErrorDialogClose,
  } = useModals({
    currentListId,
    isAuthenticated,
    userLists,
    needsUserListsReload,
    handleListChange,
    refreshUsageLimitsRef,
    onAfterDelete: () => {
      // Reset to default state after list deletion
      setTickerList([]);
      setListName('Main List');
      setHiddenStatistics([]);
      setInsiderData({});
    },
  });

  const tabs = (
    <TabBar 
      activeTab="insiders" 
      disabled={false}
      publicUuid={publicUuid}
    />
  );

  if (publicUuid && isPublicListNotFound) {
    return (
      <div className="flex flex-col min-h-screen">
        <Header />
        {!isAuthenticated && <PublicListPromoBar />}
        <div className="flex-grow flex items-center justify-center">
          <div className="text-center">
            <p className="text-lg text-gray-700 dark:text-text-primary">
              {publicListError || 'This list does not exist or is no longer available.'}
            </p>
          </div>
        </div>
        <div className="mt-4 sm:mt-8">
          <Footer />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen">
      <StockTableLayout
        tickerList={isEditingTickers ? tempTickerList : tickerList}
        tickerData={transformDataForDisplay(insiderData)}
        loading={loading || isLoadingPublicList}
        error={error || (isPublicList ? publicListError : null)}
        lastUpdated={lastUpdated}
        isEditingTickers={isPublicList ? false : isEditingTickers}
        isPublicList={isPublicList}
        showPublicListPromoBar={isPublicList && !isAuthenticated}
        hiddenStatistics={hiddenStatistics}
        draggedTicker={draggedTicker}
        dragOverTicker={dragOverTicker}
        droppedTicker={droppedTicker}
        statistics={INSIDER_TRANSACTIONS_STATISTICS}
        categoryHeaders={INSIDER_TRANSACTIONS_CATEGORY_HEADERS}
        getTickerData={getTickerData}
        formatStatisticValue={formatStatisticValue}
        getStatisticColor={getStatisticColor}
        lockedStatistics={lockedStatistics}
        proOnlyStatistics={PRO_ONLY_STATISTICS}
        lockedTickers={[]}
        tickerColumnWidth="150px"
        statisticsColumnWidthMultiplier={0.8}
        useListDailySmallScreenColumnWidth
        onRefetch={() => checkInsiderSyncAndFetchData(tickerList, 1)}
        onTickerClick={handleTickerClick}
        onRemoveTicker={isPublicList ? () => {} : handleRemoveTicker}
        onDragStart={isPublicList ? () => {} : handleDragStart}
        onDragOver={isPublicList ? () => {} : handleDragOver}
        onDragLeave={isPublicList ? () => {} : handleDragLeave}
        onDrop={isPublicList ? () => {} : handleDrop}
        onDragEnd={isPublicList ? () => {} : handleDragEnd}
        onHideStatistic={handleHideStatistic}
        onShowStatistic={handleShowStatistic}
        onCategoryInfoClick={handleCategoryInfoClick}
        onIndicatorInfoClick={() => {}}
        indicatorKeysWithInfo={[]}
        listName={listName}
        onStartEdit={isPublicList ? () => {} : handleStartEdit}
        onCancelEdit={isPublicList ? () => {} : handleCancelEdit}
        onDoneEdit={isPublicList ? () => {} : handleDoneEdit}
        onAddTicker={handleAddTicker}
        onSave={isPublicList ? () => {} : handleSave}
        tempListName={tempListName}
        onTempListNameChange={setTempListName}
        userLists={isPublicList ? [] : userLists}
        currentListId={isPublicList ? undefined : (currentListId || undefined)}
        onListChange={isPublicList ? undefined : handleListChange}
        onCreateNewList={isPublicList ? undefined : handleCreateNewList}
        onCreateNewListWithName={isPublicList ? undefined : handleCreateNewListWithName}
        onDeleteList={handleDeleteList}
        showDeleteButton={!isPublicList && isAuthenticated && userLists.length > 1}
        onShareList={handleShareList}
        showShareButton={!isPublicList && isAuthenticated && currentListId !== null && !isCurrentListLocked}
        onRefreshUsageLimits={() => {}}
        tabs={tabs}
        showRefreshButton={false}
        showDelayedPricesText={false}
        showLastUpdatedDate={true}
      />
      <div className="mt-4 sm:mt-8">
        <Footer />
      </div>
      
      {/* Category Info Modal */}
      <CategoryInfoModal
        isOpen={isCategoryModalOpen}
        onClose={handleCloseCategoryModal}
        categoryName={selectedCategory}
      />
      
      {/* Share List Modal */}
      <ShareListModal
        isOpen={isShareModalOpen}
        onClose={handleShareModalClose}
        listId={currentListId}
        currentList={currentList}
        onUpdate={handleShareModalUpdate}
        isListLocked={isCurrentListLocked}
      />
      
      {/* Delete List Confirmation Modal */}
      <DeleteListConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        listName={listName}
      />
      
      {/* Error Dialog */}
      <ErrorDialog
        isOpen={isErrorDialogOpen}
        onClose={handleErrorDialogClose}
        title="Error"
        message={errorMessage}
      />
    </div>
  );
};

export default ListInsiders;

